import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import { deleteVideo, getVideoUrl } from '../../../services/videoService';
import Icon from '../../../components/AppIcon';

const VideoTable = ({ videos, formatDuration, formatWatchTime, onRefresh }) => {
  const [deleting, setDeleting] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);

  const handleDelete = async (videoId, songTitle, level) => {
    if (!window.confirm(`Are you sure you want to delete the ${level} video for "${songTitle}"? This action cannot be undone.`)) {
      return;
    }

    setDeleting(videoId);
    
    try {
      const { error } = await deleteVideo(videoId);
      
      if (error) {
        alert(`Error deleting video: ${error}`);
      } else {
        onRefresh?.();
      }
    } catch (err) {
      console.error('Error deleting video:', err);
      alert('Failed to delete video. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  const handlePlayVideo = async (video) => {
    try {
      const videoUrl = await getVideoUrl(video?.video_path);
      if (videoUrl) {
        setPlayingVideo({
          ...video,
          url: videoUrl
        });
      } else {
        alert('Unable to load video. Please try again.');
      }
    } catch (err) {
      console.error('Error loading video:', err);
      alert('Error loading video. Please try again.');
    }
  };

  const closeVideoModal = () => {
    setPlayingVideo(null);
  };

  const getSectionLabel = (level, subsection) => {
    switch (level) {
      case 'overview':
        return 'Overview';
      case 'level1':
        return 'Level 1';
      case 'level2':
        return 'Level 2';
      case 'level3':
        return `Level 3 - ${subsection || 'General'}`;
      case 'level4':
        return `Level 4 - ${subsection || 'General'}`;
      case 'level5':
        return `Level 5 - ${subsection || 'General'}`;
      default:
        return level || 'Unknown';
    }
  };

  const getSongTitle = (video) => {
    const songTitle = video?.song_analyses?.overview?.songTitle;
    const musical = video?.song_analyses?.overview?.musical;
    
    if (songTitle && musical) {
      return `${songTitle} (${musical})`;
    } else if (songTitle) {
      return songTitle;
    } else if (musical) {
      return musical;
    }
    return 'Unknown Song';
  };

  const getTalentName = (video) => {
    const firstName = video?.talent_profiles?.first_name;
    const lastName = video?.talent_profiles?.last_name;
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    }
    return 'Not Assigned';
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString)?.toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="text-left py-4 px-6 font-semibold text-foreground">Song</th>
              <th className="text-left py-4 px-6 font-semibold text-foreground">Section</th>
              <th className="text-left py-4 px-6 font-semibold text-foreground">Talent</th>
              <th className="text-left py-4 px-6 font-semibold text-foreground">Analytics</th>
              <th className="text-left py-4 px-6 font-semibold text-foreground">Duration</th>
              <th className="text-left py-4 px-6 font-semibold text-foreground">Upload Date</th>
              <th className="text-right py-4 px-6 font-semibold text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {videos?.map((video) => (
              <tr key={video?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                <td className="py-4 px-6">
                  <div className="font-medium text-foreground">
                    {getSongTitle(video)}
                  </div>
                </td>
                
                <td className="py-4 px-6">
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary">
                    {getSectionLabel(video?.level, video?.subsection)}
                  </span>
                </td>

                <td className="py-4 px-6">
                  <div className="text-sm text-foreground">
                    {getTalentName(video)}
                  </div>
                </td>

                <td className="py-4 px-6">
                  <div className="space-y-1 text-sm">
                    <div className="text-foreground">
                      {video?.analytics?.totalPlays || 0} plays
                    </div>
                    <div className="text-muted-foreground">
                      {formatWatchTime(video?.analytics?.totalWatchTime || 0)} watched
                    </div>
                    <div className="text-muted-foreground">
                      {video?.analytics?.firstFiveSecondsPlayed || 0} engaged views
                    </div>
                  </div>
                </td>

                <td className="py-4 px-6">
                  <div className="text-sm text-muted-foreground">
                    {formatDuration(video?.duration_seconds || 0)}
                  </div>
                </td>

                <td className="py-4 px-6">
                  <div className="text-sm text-muted-foreground">
                    {formatDate(video?.created_at)}
                  </div>
                </td>

                <td className="py-4 px-6">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePlayVideo(video)}
                      className="flex items-center space-x-1"
                    >
                      <Icon name="Play" size={14} />
                      <span>Play</span>
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(video?.id, getSongTitle(video), getSectionLabel(video?.level, video?.subsection))}
                      disabled={deleting === video?.id}
                      className="flex items-center space-x-1 text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
                    >
                      <Icon name="Trash2" size={14} />
                      <span>{deleting === video?.id ? 'Deleting...' : 'Delete'}</span>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Video Modal */}
      {playingVideo && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">
                {getSongTitle(playingVideo)} - {getSectionLabel(playingVideo?.level, playingVideo?.subsection)}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeVideoModal}
                className="flex items-center space-x-1"
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
            
            <div className="p-4">
              <video
                controls
                autoPlay
                className="w-full max-h-[60vh] rounded-lg"
                src={playingVideo?.url}
              >
                Your browser does not support the video tag.
              </video>
              
              <div className="mt-4 text-sm text-muted-foreground">
                <p>Talent: {getTalentName(playingVideo)}</p>
                <p>Duration: {formatDuration(playingVideo?.duration_seconds)}</p>
                <p>Uploaded: {formatDate(playingVideo?.created_at)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VideoTable;